import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatMoney, type Centavos } from '@abastece/shared';
import { efectivoEsperado } from '@abastece/core';
import { cajasAbiertas, sesionAbierta, type CajaAbierta } from '../lib/caja.js';
import { usePuede } from '../lib/sesion.js';
import { useT } from '../i18n/index.js';
import { Interruptor, Selector } from './Campo.js';

/**
 * Paying out of the open drawer: a purchase, an expense or a supplier paid with the till's cash.
 *
 * Before this, the books had every cash payment come out of general cash, whatever hand it
 * actually came out of. Paid from the drawer, the corte came up short by exactly that much — on
 * the cashier — and general cash, which had never had the money taken out, came up over. With
 * the switch on, the payment is recorded against the open till (`sesionId`) and its corte
 * expects that much less.
 *
 * It applies with a till open and córdobas in cash, and on every form that shows it, it starts off
 * until somebody turns it on (`encendidaAlEmpezar: false`): a purchase («Mercadería comprada»; the
 * owner, 2026-10-12: «the "paid from drawer" checkbox should NOT be checked by default»), and a new
 * expense, a supplier paid from «Por pagar» and an employee's advance (2026-10-17: «the checkbox
 * that says "sale de la gaveta" shouldn't be checked by default»). Each used to start on, since
 * cash handed over at the counter comes out of the drawer. The hook still starts on by default
 * for «Cuadrar la semana»'s wizard (`PasosDeGasto`), which asks with buttons of its own and
 * never shows the switch.
 *
 * What the drawer should hold is said only to whoever receives the tills' cash, the owner. A
 * cashier counts their drawer blind at the end of the shift (`TerminarTurno.tsx`), and since a
 * cashier pays for purchases too (2026-10-09), the amount here would have told them what the
 * count is checked against: the drawer is still refused when it cannot pay, without the figure.
 *
 * `deOtroEquipo`: a till open on another device pays too, for a new expense in «Gastos» (the owner,
 * 2026-10-16: «We need an option to be able to pay an expense from the till»), which the owner
 * records from their own phone or laptop as often as at the counter. This device's till comes
 * first, starting as `encendidaAlEmpezar` says; another device's starts off, since nothing says the
 * cash came from there, and with more than one open the switch asks which (`cajas`, `elegir`).
 */
export function useGaveta(
  formaPago: string,
  { encendidaAlEmpezar = true, deOtroEquipo = false }: { encendidaAlEmpezar?: boolean; deOtroEquipo?: boolean } = {},
) {
  const aqui = useLiveQuery(() => sesionAbierta(), [], undefined);
  const todas = useLiveQuery(async () => (deOtroEquipo ? cajasAbiertas() : null), [deOtroEquipo], null);
  const cajas: CajaAbierta[] = todas ?? (aqui ? [{ sesion: aqui, quien: '', equipo: '', aqui: true }] : []);
  /** Null until somebody touches it: on or off then follows the till it would come out of. */
  const [encendido, setEncendido] = useState<boolean | null>(null);
  const [elegida, setElegida] = useState<string | null>(null);
  const verEsperado = usePuede('recibir_efectivo');
  const de = cajas.find((c) => c.sesion.id === elegida) ?? cajas[0];
  const caja = de?.sesion;
  const aplica = caja !== undefined && formaPago === 'efectivo_cordobas';
  const prendido = encendido ?? (de?.aqui ? encendidaAlEmpezar : false);
  const hay = caja ? efectivoEsperado(caja) : (0 as Centavos);
  return {
    aplica,
    encendido: prendido,
    setEncendido,
    hay,
    /** Whether what the drawer should hold may be said. */
    verEsperado,
    /** The till to send as `sesionId`, when the payment comes out of it. */
    sesionId: aplica && prendido ? caja.id : undefined,
    /** The tills it could come out of, this device's first: more than one only with `deOtroEquipo`. */
    cajas,
    /** Whether `cajas` has been read yet: none open is not said before it is known. */
    sabido: !deOtroEquipo || todas !== null,
    /** The one it comes out of, with whose drawer it is and where. */
    de,
    /** Picked, it pays: the switch stays on whoever's till it is. */
    elegir: (sesionId: string) => {
      setElegida(sesionId);
      setEncendido(true);
    },
  };
}

export type Gaveta = ReturnType<typeof useGaveta>;

/**
 * Why the drawer cannot pay `monto`, or null. The reducer refuses the event if the drawer is
 * expected to hold less, and a refused event records nothing — so the form says it first.
 */
export function faltaEnGaveta(gaveta: Gaveta, monto: number, t: ReturnType<typeof useT>): string | null {
  if (!gaveta.sesionId || monto <= gaveta.hay) return null;
  return gaveta.verEsperado
    ? t('gaveta.noAlcanza', { monto: formatMoney(gaveta.hay) })
    : t('gaveta.noAlcanzaSinMonto');
}

/** A till by whose drawer it is and where: «Karla · Mostrador». */
export const nombreDeCaja = (c: CajaAbierta, t: ReturnType<typeof useT>): string =>
  [c.quien || t('ventas.alguien'), c.equipo].filter(Boolean).join(' · ');

export function SaleDeLaGaveta({ gaveta }: { gaveta: Gaveta }) {
  const t = useT();
  if (!gaveta.aplica) return null;
  // Another device's till is said by name: the corte that stops expecting the money is someone else's.
  const otra =
    gaveta.de && !gaveta.de.aqui
      ? { quien: gaveta.de.quien || t('ventas.alguien'), equipo: gaveta.de.equipo || t('gaveta.otroEquipo') }
      : null;
  return (
    <div className="flex flex-col gap-3" data-sale-de-la-gaveta>
      <Interruptor
        etiqueta={t('gaveta.sale')}
        descripcion={
          !gaveta.encendido
            ? t('gaveta.noSaleAyuda')
            : otra
              ? gaveta.verEsperado
                ? t('gaveta.saleAyudaDe', { ...otra, monto: formatMoney(gaveta.hay) })
                : t('gaveta.saleAyudaDeSinMonto', otra)
              : gaveta.verEsperado
                ? t('gaveta.saleAyuda', { monto: formatMoney(gaveta.hay) })
                : t('gaveta.saleAyudaSinMonto')
        }
        checked={gaveta.encendido}
        onChange={gaveta.setEncendido}
      />
      {gaveta.encendido && gaveta.cajas.length > 1 && (
        <Selector
          etiqueta={t('gaveta.deCual')}
          value={gaveta.de?.sesion.id ?? ''}
          onChange={(e) => gaveta.elegir(e.target.value)}
          data-gaveta-cual
        >
          {gaveta.cajas.map((c) => (
            <option key={c.sesion.id} value={c.sesion.id}>
              {c.aqui ? t('gaveta.deEsteEquipo', { caja: nombreDeCaja(c, t) }) : nombreDeCaja(c, t)}
            </option>
          ))}
        </Selector>
      )}
    </div>
  );
}
